import { report } from '../report';
import { ReadonlyDate } from './Types';

export enum Weekday {
    SUN = 0,
    MON = 1,
    TUE = 2,
    WED = 3,
    THU = 4,
    FRI = 5,
    SAT = 6,
}

const REGEX = /(D{1,2})|(h{1,2})|(m{1,2})|(s{1,2})/g;
const WEEKLY_RESET_DATE: number = 4; // Thursday

export default class DateHelper {
    public static Minute: number = 60 * 1000;
    public static Hour: number = 60 * 60 * 1000;
    public static HalfDay: number = DateHelper.Hour * 12;
    public static Day: number = DateHelper.HalfDay * 2;
    public static TwoDay: number = DateHelper.Day * 2;
    public static Week: number = DateHelper.Day * 7;

    public static get now(): Date {
        return getDate();
    }

    public static getDateFromValue(value: number | string): Date {
        return new Date(value);
    }

    public static getDate(value: number | string) {
        return new Date(value);
    }

    public static timeTo(
        from: ReadonlyDate,
        to: ReadonlyDate,
        format: string = 'mm:ss'
    ): string {
        if (from === null || to === null) {
            console.warn(
                `Invaild date of 'null'. 'timeTo' can not be parsed`,
                `From: ${from}`,
                `To: ${to}`
            );
            return '99:99';
        }
        const inititalTime: number = from.getTime();
        const endTime: number = to.getTime();

        return DateHelper.format(endTime - inititalTime, format);
    }

    public static getTimeUntil(from: ReadonlyDate, to: ReadonlyDate): number {
        if (from === null || to === null) {
            console.warn(
                `Invalid date of 'null'. 'getTimeUntil' can not be parsed`,
                `From: ${from}`,
                `To: ${to}`
            );
            return 0;
        }
        const initialTime: number = from.getTime();
        const endTime: number = to.getTime();

        return endTime - initialTime;
    }

    public static timeSince(time: ReadonlyDate | null): number {
        if (time === null) {
            report(`Invaild date of 'null'. 'timeSince' can not be parsed`, {
                sampleRate: 0.1,
                once: true,
            });
            return 0;
        }
        const currentTime: number = DateHelper.now.getTime();
        const startTime: number = time.getTime();

        return currentTime - startTime;
    }

    public static daysSince(time: ReadonlyDate): number {
        const newDate: Date = new Date(
            new Date(time.getTime()).setHours(0, 0, 0)
        );
        return Math.floor(
            Math.abs(DateHelper.timeSince(newDate)) / DateHelper.Day
        );
    }

    public static format(time: number, format: string): string {
        const D = Math.floor(time / (24 * 60 * 60 * 1000));
        let h = Math.floor(time / (60 * 60 * 1000));
        let m = Math.floor(time / (60 * 1000)) % 60;
        let s = Math.floor(time / 1000) % 60;

        return format.replace(REGEX, match => {
            const len = match.length;
            switch (match[0]) {
                case 'D':
                    h = h % 24;
                    return len === 1 ? D.toString() : ('0' + D).slice(-2);
                case 'h':
                    m = m % 60;
                    return len === 1 ? h.toString() : ('0' + h).slice(-2);
                case 'm':
                    s = s % 60;
                    return len === 1 ? m.toString() : ('0' + m).slice(-2);
                case 's':
                    return len === 1 ? s.toString() : ('0' + s).slice(-2);
                default:
                    return match;
            }
        });
    }

    public static secondsToMilliseconds(seconds: number): number {
        return seconds * 1000;
    }

    public static minutesToMilliseconds(minutes: number): number {
        return minutes * 60 * 1000;
    }

    public static hoursToMilliseconds(hours: number): number {
        return hours * 60 * 60 * 1000;
    }

    public static daysToMilliseconds(days: number): number {
        return days * 24 * 60 * 60 * 1000;
    }

    public static getPreviousWeekday(weekday: Weekday, date: Date, weeks = 1): Date {
        const newDate = new Date(date.valueOf());
        newDate.setUTCHours(0);
        newDate.setUTCMinutes(0);
        newDate.setUTCSeconds(0);
        newDate.setUTCMilliseconds(0);

        const diff = ((7 * weeks) - weekday + newDate.getUTCDay()) % (7 * weeks);
        newDate.setUTCDate(newDate.getUTCDate() - diff);

        return newDate;
    }

    /** Returns the day of the date given */
    public static getDay(date: ReadonlyDate | null): Date {
        return date !== null
            ? new Date(new Date(date.getTime()).setHours(0, 0, 0, 0))
            : null;
    }

    /** Returns the start of the day */
    public static get today(): Date {
        return new Date(DateHelper.now.setHours(0, 0, 0, 0));
    }

    /**
     * Returns the start of the week after the date given
     * aka next Thursday at 21:00
     * @param date - Date to get next week of
     */
    public static getNextWeek(date: ReadonlyDate): Date {
        const nextWeek: Date = DateHelper.now;
        nextWeek.setDate(
            DateHelper.now.getDate() +
            ((WEEKLY_RESET_DATE + 7 - DateHelper.now.getDay()) % 7)
        );
        nextWeek.setHours(21, 0, 0, 0);
        return nextWeek;
    }

    /** Gets the date for the next week according to the current time */
    public static get nextWeek(): Date {
        return this.getNextWeek(DateHelper.now);
    }

    /** Returns the start of the day before the date given */
    public static getPreviousDay(date: ReadonlyDate): Date {
        const newDate: Date = new Date(date.getTime());
        return new Date(
            new Date(newDate.setDate(date.getDate() - 1)).setHours(0, 0, 0, 0)
        );
    }

    /** Returns the start of the day after the date given */
    public static getNextDay(date: ReadonlyDate): Date {
        const newDate: Date = new Date(date.getTime());
        return new Date(
            new Date(newDate.setDate(date.getDate() + 1)).setHours(0, 0, 0, 0)
        );
    }

    /** Gets the date for yesterday according to the current time */
    public static get yesterday(): Date {
        return this.getPreviousDay(DateHelper.now);
    }

    public static isCompleted(time: ReadonlyDate, duration: number, defaultValue: boolean = false): boolean {
        if (time === null || time === undefined) return defaultValue;
        const timeDiff = DateHelper.now.getTime() - time.getTime();
        return timeDiff >= duration;
    }
    public static weeksSinceEpoch(date: ReadonlyDate, offset: number = 0): number {
        return (date) ? Math.floor((date.getTime() + offset) / DateHelper.Week) : 0;
    }

    /**
     * Return true if now() is between a range of dates (start and end).
     * @param startDate
     * @param endDate
     * @returns
     */
    public static isBetween(startDate: string | number | Date, endDate: string | number | Date): boolean {
        const start = new Date(startDate).getTime();
        const end = new Date(endDate).getTime();
        const now = DateHelper.now.getTime();
        return start <= now && now <= end;
    }

    public static getElapsedTime(targetDate: number | string): number {
        return DateHelper.now.getTime() - DateHelper.getDateFromValue(targetDate).getTime();
    }
}

function pad(num: string | number, zeroes: number = 2) {
    return ('0'.repeat(zeroes) + num.toString()).slice(-zeroes);
}

/** Get a UTC date string in the format yyyy-mm-dd hh:mm:ss.SSS */
export function getDateString() {
    const date = DateHelper.now;
    return (
        date.getUTCFullYear() +
        '-' +
        pad(date.getUTCMonth() + 1) +
        '-' +
        pad(date.getUTCDate()) +
        ' ' +
        pad(date.getUTCHours()) +
        ':' +
        pad(date.getUTCMinutes()) +
        ':' +
        pad(date.getUTCSeconds()) +
        '.' +
        pad(date.getUTCMilliseconds(), 3)
    );
}

export function getTimeZone() {
    return /\((.*)\)/.exec(new Date().toString())[1];
}

export function getTime() {
    return DateHelper.now.toTimeString().split(' ')[0];
}

export function getISOTime() {
    return DateHelper.now.toISOString().substring(11);
}

/**
 * Get an SQL date format yyyy-mm-dd
 */
export function getSQLDate(date?: Date): string {
    date = date || getDate();
    return date.getUTCFullYear() + '-' + pad(date.getUTCMonth() + 1) + '-' + pad(date.getUTCDate());
}

let relativeDate: Date;
let startMS: number;

/**
 * Set the standard time retrieved from the server.
 * @param timestamp Server generated Unix timestamp in milliseconds.
 */
export function setServerDate(timestamp: number): void {
    relativeDate = new Date(timestamp);
    startMS = Math.round(performance.now());
}

/**
 * Get a date object using relativeDate from server if it exists, otherwise local date.
 */
export function getDate(): Date {
    if (relativeDate !== undefined) {
        const relativeDateMS = +relativeDate;
        const endMS = Math.round(performance.now());

        // Add elapsed MS using `performance.now()`.
        return new Date(relativeDateMS + endMS - startMS);
    }

    return new Date();
}

/**
 * Get a date string in the cheats format yyyy.mm.dd.hh.mm.ss
 */
export function getCheatsDate(date?: Date): string {
    date = date || getDate();
    return formatCheatDate(date);
}

export function formatCheatDate(date: Date): string {
    return (
        date.getFullYear() +
        '.' +
        pad(date.getMonth() + 1) +
        '.' +
        pad(date.getDate()) +
        '.' +
        pad(date.getHours()) +
        '.' +
        pad(date.getMinutes()) +
        '.' +
        pad(date.getSeconds())
    );
}

export function parseCheatDate(value: string): Date {
    const numbers = value.split('.');
    const year = Number(numbers[0]);
    const month = Number(numbers[1]) - 1;
    const day = Number(numbers[2]);
    const hours = Number(numbers[3]);
    const minutes = Number(numbers[4]);
    const seconds = Number(numbers[5]);
    return new Date(year, month, day, hours, minutes, seconds);
}
